import React, { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";

const STORAGE_KEY = "wp_cookie_consent_v1";

/**
 * Minimal, honest cookie consent banner. WarPaint DFS does NOT use
 * third-party ad trackers or analytics pixels — only first-party
 * auth + UI-preference storage — so we don't need a full CMP with
 * granular toggles. This banner exists to comply with EU/CA/UK
 * cookie-notice laws that require an explicit acknowledgement of
 * any localStorage / cookie usage. One "Got it" click, dismissed
 * for 12 months. Never shows again unless localStorage is cleared.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const { ts } = JSON.parse(raw);
        const age = Date.now() - (ts || 0);
        if (age < 365 * 24 * 60 * 60 * 1000) return; // still fresh
      }
      setVisible(true);
    } catch (_) {
      setVisible(true);
    }
  }, []);
  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now() }));
    } catch (_) { /* private mode → banner just doesn't dismiss, that's fine */ }
    setVisible(false);
  };
  if (!visible) return null;
  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-[9999] border-2 border-[#39FF14] bg-black text-white shadow-2xl"
      style={{ boxShadow: "0 8px 32px rgba(57,255,20,0.25)" }}
      data-testid="cookie-consent-banner"
    >
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="flex-1 min-w-0 text-[12px] leading-relaxed">
          <div className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-black mb-1">
            Cookie notice
          </div>
          <div className="text-zinc-200">
            WarPaint DFS uses first-party storage for authentication and remembering your slate
            preferences. We do <strong className="text-white">not</strong> use third-party
            advertising trackers or analytics pixels. See our{" "}
            <a href="/privacy" className="text-[#39FF14] underline">Privacy Policy</a>{" "}
            for details.
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="cookie-consent-accept"
            className="mt-3 bg-[#39FF14] hover:bg-[#2ee30d] text-black font-black uppercase tracking-widest text-[10px] px-3 py-1.5"
          >
            Got it
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="text-zinc-500 hover:text-white flex-shrink-0"
          data-testid="cookie-consent-dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
